<template>
    <div>
        <!-- Main layout -->
        <draggable
            id="special-conditions"
            :list="specialConditions"
            :options="{group: {name: 'specialConditionLayout'}}"

            :class="[openBar ? 'closed': 'open', specialConditions.length === 0 ? 'dropzone': '']"
            @sort="onSort"
            :delay="200"
            :delay-on-touch-only="true"
        >
            <div v-for="(specialCondition, index) in specialConditions" class="accordion">
                <h3 class="hidden">{{ specialCondition.title }}</h3>
                <input class="hidden" type="checkbox" v-model="specialCondition.show"
                       :disabled="specialCondition.disabled">
                <validation-observer v-slot="{failed}" slim>
                    <div
                        :class="{ open : editableCondition === specialCondition, invalid: failed }"
                        class="paragraph-box"
                    >
                        <!-- Title form -->
                        <div class="top validation-form-group">
                            <svg
                                @click="toggleSpecialConditionEditor(specialCondition, 'specialCondition' + index)"
                                class="handle"
                                xmlns="http://www.w3.org/2000/svg"
                                width="9.895" height="19.875"
                                viewBox="0 0 19.79 39.75">
                                <path fill="#d0d0d0"
                                      d="M1326.31,605.314a1.99,1.99,0,0,1-2.82,0l-6.49-6.486-6.49,6.486a2,2,0,1,1-2.82-2.829l7.8-7.8a2.267,2.267,0,0,1,3.02,0l7.8,7.8A2,2,0,0,1,1326.31,605.314Z"
                                      transform="translate(-1307.12 -594.125)"/>
                                <path fill="#d0d0d0"
                                      d="M1326.31,625.515l-7.8,7.8a2.267,2.267,0,0,1-3.02,0l-7.8-7.8a2,2,0,1,1,2.82-2.829l6.49,6.486,6.49-6.486A2,2,0,1,1,1326.31,625.515Z"
                                      transform="translate(-1307.12 -594.125)"/>
                            </svg>

                            <p
                                class="paragraph-title"
                                @click="toggleSpecialConditionEditor(specialCondition, 'specialCondition' + index)"
                            >
                                §11, stk. {{ parseInt(index) + 1 }}
                                <br>
                                <span class="top-title">{{ specialCondition.title }}</span>
                            </p>

                            <div
                                class="warning-info"
                                v-if="hasFillableParameters(specialCondition.description)"
                                @click="toggleSpecialConditionEditor(specialCondition, 'specialCondition' + index)"
                            >
                                <SvgIcon icon="warning-icon"/>
                                <p>{{ $t('label.condition.fill') }}</p>
                            </div>

                            <button
                                class="small-button"
                                v-on:click="saveUserSpecialCondition(specialCondition)"
                                :disabled="specialCondition.$invalid"
                            >
                                {{ $t('button.conditions.save') }}
                            </button>

                            <div
                                class="delete"
                                @click="removeSpecialCondition(specialCondition)"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg"
                                     width="11.895"
                                     height="11.89"
                                     viewBox="0 0 23.79 23.781"
                                >
                                    <path fill="#d0d0d0"
                                          d="M2090.83,614l8.48,8.485a2,2,0,0,1-2.82,2.829l-8.49-8.486-8.49,8.486a2,2,0,0,1-2.82-2.829l8.48-8.485-8.48-8.485a2,2,0,0,1,2.82-2.829l8.49,8.486,8.49-8.486a2,2,0,0,1,2.82,2.829Z"
                                          transform="translate(-2076.12 -602.125)"
                                    />
                                </svg>
                            </div>
                        </div>
                        <!-- / Title Form -->

                        <!-- Opened form -->
                        <div class="container" v-if="editableCondition === specialCondition">
                            <div class="title validation-form-group">
                                <label>{{ $t('label.title') }}</label>
                                <validation-provider rules="required" immediate slim>
                                    <input
                                        type="text"
                                        @change="saveDraft"
                                        :placeholder="$t('label.title-placeholder')"
                                        v-model="specialCondition.title"
                                    />
                                </validation-provider>
                                <div class="warning-info invalid-message">
                                    <SvgIcon icon="warning-icon"/>
                                    <p>{{ $t('label.condition.fill') }}</p>
                                </div>
                            </div>
                            <div class="content validation-form-group">
                                <label>{{ $t('label.description') }}</label>

                                <div contenteditable="true"
                                     @focusout="specialCondition.description = $event.target.innerText"
                                     :inner-html.prop="specialCondition.description | yellowBlocks"
                                     class="textarea"
                                />

                                <div class="warning-info invalid-message">
                                    <SvgIcon icon="warning-icon"/>
                                    <p>{{ $t('label.condition.fill') }}</p>
                                </div>
                            </div>
                        </div>
                        <!-- / Opened form -->
                    </div>
                </validation-observer>
            </div>

        </draggable>
        <!-- / Main layout -->

        <!-- Add button -->
        <div class="wizard-component">
            <div class="add-more">
                <button type="button" class="green" style="margin-top: 30px;" @click="addCustomSpecialCondition">
                    <SvgIcon icon="plus-circle"/>
                    {{ $t('button.custom-conditions') }}
                </button>
            </div>
        </div>
        <!-- / Add button -->

        <div class="toggle-bar on-mobile" :class="openBar ? 'closed': 'open'" @click="openBar = !openBar">
            <p>{{ $t('add.special-conditions.tab') }}</p>
            <SvgIcon icon="select-arrow"/>
        </div>

        <div class="add-bar" :class="openBar ? 'closed': 'open'">
            <div class="toggle-bar" @click="openBar = !openBar">
                <div class="icon">
                    <SvgIcon icon="toggle"/>
                </div>
            </div>
            <div class="content">
                <h2>{{ $t('add.special-conditions.headline') }}</h2>
                <p>{{ $t('add.special-conditions.text') }}</p>

                <div class="search-bar">
                    <form>
                        <SvgIcon icon="search"/>
                        <input
                            type="search"
                            v-model="specialConditionFilterText"
                            :placeholder="$t('search.placeholder')"
                            autofill="off"
                            autocomplete="false"
                        />
                    </form>
                </div>

                <div class="seperator"></div>

                <!-- User Special Conditions -->
                <template v-if="userSpecialConditions.length > 0" class="category accordion">
                    <label class="toggle" :class="hideUserSpecialconditions ? 'closed': 'open'">
                        <p class="subject">{{ $t('special-conditions.my-templates.label') }}</p>
                        <input class="hidden" type="checkbox" v-model="hideUserSpecialconditions">
                        <SvgIcon icon="small-arrow-up"/>
                    </label>

                    <draggable
                        v-if="!hideUserSpecialconditions"
                        id="user-special-condition-templates"
                        :list="userSpecialConditions"
                        :options="{group: {name: 'userSpecialConditions', pull: ['specialConditionLayout']}}"
                        @end="onEnd"
                        :delay="200"
                        :delay-on-touch-only="true"
                    >
                        <label
                            v-if="showInFilter(userSpecialCondition.title)"
                            v-for="userSpecialCondition in userSpecialConditions"
                            class="choose-condition"
                            data-special-condition-type="App/Models/UserSpecialCondition"
                            :data-special-condition-id="userSpecialCondition.id"
                        >

                            <div class="small-checkbox">
                                <input
                                    type="checkbox"
                                    :checked="hasSpecialCondition(userSpecialCondition.id,'App/Models/UserSpecialCondition')"
                                    v-on:change="toggleSpecialCondition(userSpecialCondition, 'App/Models/UserSpecialCondition')"/>
                                <div class="checkbox">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="10.395" height="7.875"
                                         viewBox="0 0 20.79 15.75">
                                        <path fill="#2fc07d"
                                              d="M3339.31,565.515l-11.8,11.8a2.267,2.267,0,0,1-3.02,0l-4.8-4.8a2,2,0,0,1,2.82-2.829l3.49,3.486,10.49-10.486A2,2,0,0,1,3339.31,565.515Z"
                                              transform="translate(-3319.12 -562.125)"/>
                                    </svg>
                                </div>
                            </div>

                            <p>{{ userSpecialCondition.title }}</p>

                            <div class="delete-button"
                                 v-on:click="deleteUserSpecialCondition(userSpecialCondition.id)">
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12" viewBox="0 0 20 24">
                                    <path fill="#ffffff"
                                          d="M3752,580a2,2,0,0,0,2,2h12a2,2,0,0,0,2-2V566h-16v14Zm16-20h-4a2,2,0,0,0-2-2h-4a2,2,0,0,0-2,2h-4a2,2,0,0,0,0,4h16A2,2,0,0,0,3768,560Zm-11,9a2,2,0,0,1,2,2v6a2,2,0,0,1-4,0v-6A2,2,0,0,1,3757,569Zm6,0a2,2,0,0,1,2,2v6a2,2,0,0,1-4,0v-6A2,2,0,0,1,3763,569Z"
                                          transform="translate(-3750 -558)"/>
                                </svg>
                            </div>
                        </label>
                    </draggable>
                </template>

                <div class="seperator" v-if="userSpecialConditions.length > 0"/>
                <!-- / User Special Conditions -->


                <!-- Special Conditions -->
                <div class="category accordion" v-for="(group, i ) in specialConditionGroups" :key="i">
                    <label class="toggle" :class="group.hide ? 'closed': 'open'">
                        <p class="subject">{{ group.title }}</p>
                        <input class="hidden" type="checkbox" v-model="group.hide">
                        <SvgIcon icon="small-arrow-up"/>
                    </label>
                    <draggable
                        v-if="!group.hide"
                        id="special-condition-templates"
                        :list="group.special_conditions"
                        :options="{group: {name: 'specialConditions', pull: ['specialConditionLayout']}}"
                        @end="onEnd"
                        :delay="200"
                        :delay-on-touch-only="true"
                    >
                        <label
                            v-if="showInFilter(specialCondition.title)"
                            v-for="specialCondition in group.special_conditions"
                            class="choose-condition"
                            data-special-condition-type="App/Models/SpecialCondition"
                            :data-special-condition-id="specialCondition.id"
                        >
                            <div class="small-checkbox">
                                <input type="checkbox"
                                       :checked="hasSpecialCondition(specialCondition.id, 'App/Models/SpecialCondition')"
                                       v-on:change="toggleSpecialCondition(specialCondition, 'App/Models/SpecialCondition')"/>
                                <div class="checkbox">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="10.395" height="7.875"
                                         viewBox="0 0 20.79 15.75">
                                        <path fill="#2fc07d"
                                              d="M3339.31,565.515l-11.8,11.8a2.267,2.267,0,0,1-3.02,0l-4.8-4.8a2,2,0,0,1,2.82-2.829l3.49,3.486,10.49-10.486A2,2,0,0,1,3339.31,565.515Z"
                                              transform="translate(-3319.12 -562.125)"/>
                                    </svg>
                                </div>
                            </div>
                            <p>{{ specialCondition.title }}</p>
                        </label>
                    </draggable>

                </div>
                <!-- / Special Conditions -->
            </div>
        </div>
    </div>
</template>

<script>

/**
 * Relevant documentations:
 * @see https://github.com/SortableJS/Vue.Draggable
 */

import {mapGetters} from 'vuex';
import draggable from 'vuedraggable'
import SvgIcon from '../../../components/Svg.vue';

export default {
    data: () => ({
        userSpecialConditions: [],
        specialConditions: [],
        specialConditionGroups: [],
        protectedSpecialConditionIds: [], // Protect Special Conditions enabled from the outside with prompt

        // GUI

        specialConditionFilterText: '',
        editableCondition: null,
        openBar: false,
        hideUserSpecialconditions: false
    }),
    props: ['value'],
    components: {
        draggable,
        SvgIcon
    },
    computed: {
        ...mapGetters([
            'getSpecialConditions',
            'getSingleDraftContract',
            'getToken'
        ])
    },
    filters: {
        yellowBlocks: (value) => {
            return value.replace(/\[([^\]]+)\]/g, '<span style="background:#fae090">[$1]</span>');
        }
    },
    methods: {
        divInput(event, specialCondition) {
            specialCondition.description = event.target.innerText;
        },
        hasFillableParameters(text) {
            return text !== null && text.match(/\[.*?\]/gm);
        },

        /* User Custom Special Conditions */

        fetchUserSpecialConditions() {
            axios({
                method: 'GET',
                url: window.config.apiUrl + 'api/user-special-condition',
                cancelToken: window.AuthorizationCancelSource.token,
            }).then(response => {
                this.userSpecialConditions = response.data;
            })
        },

        saveUserSpecialCondition(specialCondition) {
            axios({
                method: 'POST',
                url: window.config.apiUrl + 'api/user-special-condition',
                cancelToken: window.AuthorizationCancelSource.token,
                data: {
                    'title': specialCondition.title,
                    'content': specialCondition.description
                }
            }).then(response => {
                this.$store.commit('setSuccessBarMessage', this.$t('special-conditions.user-template-added.label'));
                this.fetchUserSpecialConditions();
            })
        },

        deleteUserSpecialCondition(id) {
            if (confirm(this.$t('add.special-conditions.confirm-delete-user-template'))) {
                axios({
                    method: 'DELETE',
                    url: window.config.apiUrl + 'api/user-special-condition/' + id,
                    cancelToken: window.AuthorizationCancelSource.token,
                }).then(response => {
                    this.fetchUserSpecialConditions();
                    this.$store.commit('setSuccessBarMessage', this.$t('add.special-condition.user-template-deleted'));
                })
            }
        },

        enableUserSpecialCondition(id) {
            let specialCondition = _(this.userSpecialConditions)
                .find({id: parseInt(id)});

            if (!this.hasSpecialCondition(specialCondition.id, 'App/Models/UserSpecialCondition')) {
                this.specialConditions.push({
                    'id': this.specialConditions.length,
                    'title': specialCondition.title,
                    'description': specialCondition.content,
                    'special_condition_type': 'App/Models/UserSpecialCondition',
                    'special_condition_id': specialCondition.id,
                    'show': true,
                    'disabled': false
                });
            }
        },

        disableUserSpecialCondition(id) {
            this.$delete(this.specialConditions, _.findIndex(this.specialConditions, {
                special_condition_id: id,
                special_condition_type: 'App/Models/UserSpecialCondition'
            }));
        },

        /* / User Custom Special Conditions */

        /* Predefined Special Conditions (external use) */

        fetchSpecialConditionGroups() {
            axios({
                method: 'GET',
                url: window.config.apiUrl + 'api/special-condition-group',

            }).then(response => {
                this.specialConditionGroups = response.data;
            });
        },

        enableSpecialCondition(id, emitEvent = true) {
            let specialCondition = _(this.specialConditionGroups)
                .flatMap((specialConditionGroup) => specialConditionGroup.special_conditions)
                .find({id: parseInt(id)});

            if (!this.hasSpecialCondition(specialCondition.id, 'App/Models/SpecialCondition')) {
                this.specialConditions.push({
                    'id': this.specialConditions.length,
                    'title': specialCondition.title,
                    'description': specialCondition.description ?? specialCondition.content,
                    'special_condition_type': 'App/Models/SpecialCondition',
                    'special_condition_id': specialCondition.id,
                    'show': true,
                    'disabled': false
                });
            }
            if (emitEvent) {
                this.$emit('enable', specialCondition.id);
            }
        },

        disableSpecialCondition(id, emitEvent = true) {
            this.$delete(this.specialConditions, _.findIndex(this.specialConditions, {
                special_condition_id: id,
                special_condition_type: 'App/Models/SpecialCondition'
            }));

            if (emitEvent) {
                this.$emit('disable', id);
            }
        },

        syncSpecialCondition(id, enable) {
            console.debug('SpecialConditions.vue::syncSpecialCondition', id, enable);

            if (enable) {
                this.enableSpecialCondition(id, false);
                this.protectedSpecialConditionIds[id] = true;
            } else {
                this.disableSpecialCondition(id, false);
                this.protectedSpecialConditionIds[id] = false;
            }
        },

        insertSpecialConditionDate(id, date) {
            if (!moment(date).isValid())
                return;

            let specialConditionIndex = _.findIndex(this.specialConditions, {
                special_condition_id: id,
                special_condition_type: 'App/Models/SpecialCondition'
            });

            let newDescription = this.specialConditions[specialConditionIndex]
                .description
                .replace('[dd/mm/åå]', moment(date).utc().format('DD-MM-YYYY'))
                .replace('[00:00]', moment(date).utc().format('HH:mm'))
                .replace('[12:00]', moment(date).utc().format('HH:mm'));

            let specialCondition = this.specialConditions[specialConditionIndex];
            specialCondition.description = newDescription;

            this.$set(this.specialConditions, specialConditionIndex, specialCondition);
        },

        /* / Predefined Special Conditions (external use) */

        /* Custom Special Conditions */

        addCustomSpecialCondition() {
            console.debug('SpecialConditions.vue::addCustomSpecialCondition');

            this.specialConditions.push({
                'id': this.specialConditions.length,
                'title': '',
                'description': '',
                'special_condition_type': null,
                'special_condition_id': null,
                'show': true,
                'disabled': false
            });
            this.saveDraft();
        },

        removeCustomSpecialCondition(id) {
            console.debug('SpecialConditions.vue::removeCustomSpecialCondition');

            this.$delete(this.specialConditions, _.findIndex(this.specialConditions, {id: id}));
            this.saveDraft();
        },

        /* / Custom Special Conditions */

        /* Common methods for User and predefined Special Conditions */

        hasSpecialCondition(id, type = null) {
            return _.findIndex(this.specialConditions, {
                special_condition_id: id,
                special_condition_type: type
            }) >= 0;
        },

        toggleSpecialCondition(specialCondition, type) {
            console.debug('SpecialConditions.vue::toggleSpecialCondition');

            if (this.hasSpecialCondition(specialCondition.id, type)) {
                if (type === 'App/Models/SpecialCondition') {
                    this.disableSpecialCondition(specialCondition.id)
                }

                if (type === 'App/Models/UserSpecialCondition') {
                    this.disableUserSpecialCondition(specialCondition.id)
                }

            } else {
                if (type === 'App/Models/SpecialCondition') {
                    this.enableSpecialCondition(specialCondition.id)
                }

                if (type === 'App/Models/UserSpecialCondition') {
                    this.enableUserSpecialCondition(specialCondition.id)
                }
            }

            this.saveDraft();
        },

        removeSpecialCondition(specialCondition) {

            let type = specialCondition.special_condition_type;
            let id = specialCondition.special_condition_id ?? specialCondition.id;

            if (type === 'App/Models/SpecialCondition') {

                if (this.protectedSpecialConditionIds[specialCondition.id]) {
                    if (!confirm('Removing this special condition will affect the contract state')) {
                        return;
                    }
                }
                this.disableSpecialCondition(id);
            }

            if (type === 'App/Models/UserSpecialCondition') {
                this.disableUserSpecialCondition(id);
            }

            if (type === null) {
                this.removeCustomSpecialCondition(id);
            }
        },

        /* / Common methods for User and predefined Special Conditions */

        /* GUI */

        onSort() {
            console.debug('SpecialConditions.vue::onSort');

            this.saveDraft();
        },

        onEnd(event) {
            console.debug('SpecialConditions.vue::onEnd');

            if (!this.hasSpecialCondition(event.item.dataset.specialConditionId, event.item.dataset.specialConditionType)) {
                if (event.item.dataset.specialConditionType === 'App/Models/SpecialCondition') {
                    this.enableSpecialCondition(event.item.dataset.specialConditionId);
                }

                if (event.item.dataset.specialConditionType === 'App/Models/UserSpecialCondition') {
                    this.enableUserSpecialCondition(event.item.dataset.specialConditionId);
                }
            }

            this.saveDraft();
        },

        saveDraft() {
            console.debug('SpecialConditions.vue::saveDraft');

            this.$emit('input', this.value);
            this.$emit('save');
        },

        showInFilter(title) {
            if (this.specialConditionFilterText === '')
                return true;

            return title.toLowerCase().indexOf(this.specialConditionFilterText.toLowerCase()) >= 0;
        },

        toggleSpecialConditionEditor(specialCondition, textareaId) {
            if (specialCondition === this.editableCondition) {
                this.editableCondition = null;
            } else {
                this.editableCondition = specialCondition;


            }
        }
    },
    created() {
        this.fetchSpecialConditionGroups();
        this.fetchUserSpecialConditions();
    },
    mounted() {
        this.specialConditions = this.value.specialConditions;
    }
}
</script>


<style scoped>
#special-conditions.dropzone:before {
    content: 'Træk og slip vilkår hertil';
    position: relative;
    width: 100%;
    display: block;
    text-align: center;
    top: 10px;
    font-family: HankenGroteskSemiBold, HankenGroteskSemiBoldOld, HelveticaNeue, Helvetica, Arial, sans-serif;
    color: #1c073e;
}
</style>
